import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Star from '@/components/atoms/Star/Star';
import Tooltip from '@/components/atoms/UI/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import useIsTouchDevice from '@/core/hooks/useIsTouchDevice';

export const Wrap = styled.div<{ starSize: number, autoWidth?: boolean }>`
  display: flex;
  width: fit-content;
  align-items: center;
  flex-direction: row;
  // justify-content: ${({ autoWidth }) => (autoWidth ? 'center' : 'space-between')};
  margin-top: ${({ starSize }) => starSize + 4}rem;
  gap: 2rem;
  cursor: pointer;
  position: relative;
`;

export const StarWrapper = styled.div<{ starSize: number }>`
  display: inline-block;
  width: ${({ starSize }) => starSize}rem;
  height: ${({ starSize }) => starSize}rem;
  position: relative;
`;

interface StarRatingSelectorProps {
	maxRating?: number;
	onRatingSelect?: (rating: number) => void;
	starSize?: number;
	selectedRating?: number;
	autoWidth?: boolean;
}

const StarRatingSelector: React.FC<StarRatingSelectorProps> = ({
	                                                               maxRating = 5,
	                                                               onRatingSelect,
	                                                               starSize = 3,
	                                                               selectedRating = 0,
	                                                               autoWidth = false,
                                                               }) => {
	const [hoveredRating, setHoveredRating] = useState<number | null>(null);
	const [currentRating, setCurrentRating] = useState<number>(selectedRating);
	const [showPrompt, setShowPrompt] = useState<boolean>(selectedRating === 0);

	const isTouchDevice = useIsTouchDevice();

	const { t } = useTranslation();
	const ratingTitles = useMemo(
		() => [
			t('rating.title.very_bad'),
			t('rating.title.bad'),
			t('rating.title.normal'),
			t('rating.title.good'),
			t('rating.title.excellent'),
		],
		[t]
	);

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isTouchDevice) return;

		const { left, width } = e.currentTarget.getBoundingClientRect();
		const relativeX = e.clientX - left;
		const starWidth = width / maxRating;
		const hoverIndex = Math.ceil(relativeX / starWidth);
		setHoveredRating(hoverIndex);
		setShowPrompt(false);
	};

	const handleMouseLeave = () => {
		if (isTouchDevice) return;
		setHoveredRating(null);
		if (selectedRating === 0) {
			setShowPrompt(true);
		}
	};

	const handleClick = (rating: number) => {
		setCurrentRating(rating);
		if (onRatingSelect) {
			onRatingSelect(rating);
		}
		setShowPrompt(false);
	};

	useEffect(() => {
		setCurrentRating(selectedRating);

		if (selectedRating > 0) {
			setShowPrompt(false);
		} else {
			setShowPrompt(true);
		}
	}, [selectedRating]);

	return (
		<Wrap
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			starSize={starSize}
			autoWidth={autoWidth}
		>
			{showPrompt && (
				<Tooltip centered arrowDown visible={showPrompt}>
					{t('rating.prompt')}
				</Tooltip>
			)}

			{[...Array(maxRating)].map((_, index) => {
				const fillPercentage =
					(hoveredRating !== null ? hoveredRating : currentRating) > index ? 100 : 0;

				return (
					<StarWrapper
						key={index}
						starSize={starSize}
						onClick={() => handleClick(index + 1)}
					>
						<Star fillPercentage={fillPercentage} index={index} />
						<Tooltip
							centered
							arrowDown
							visible={
								(hoveredRating !== null && hoveredRating - 1 === index) ||
								(hoveredRating === null && selectedRating > 0 && selectedRating - 1 === index)
							}
						>
							{ratingTitles[index]}
						</Tooltip>
					</StarWrapper>
				);
			})}
		</Wrap>
	);
};

export default StarRatingSelector;
